import type { ApolloClient } from '@apollo/client';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { last } from 'lodash';

import type { UIMessageWithMetadata } from './types';
import {
  GET_CHAT_WITH_MESSAGES,
  GET_CHATS_BY_FILTERS,
  type ChatAppType,
  type GetChatWithMessagesMessage,
} from '../../../queries/chats';

type CacheableUIMessage = UIMessageWithMetadata & {
  uuid?: string;
  chatUuid?: string;
  createdAt?: string | Date;
};

/**
 * Normalizes a chat UUID by removing the 'new-chat-' prefix if present.
 * This ensures consistent UUID handling across the codebase.
 */
export function normalizeChatUuid(chatUuid: string): string {
  return chatUuid.replace('new-chat-', '');
}

function buildOptimisticStreamId(chatUuid: string): string {
  return `pending-stream-${normalizeChatUuid(chatUuid)}`;
}

function buildOptimisticFileUuid(messageId: string, fileIndex: number): string {
  return `${messageId}-file-${fileIndex}`;
}

/**
 * Builds notification text from chat data.
 * Prefers the last assistant message text, falling back to chat title.
 */
export async function buildNotificationText({
  apolloClient,
  chatUuid,
}: {
  apolloClient: ApolloClient<unknown>;
  chatUuid: string;
}): Promise<string> {
  const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);
  const chatTitleCallback = 'You have a new message';

  try {
    const { data } = await apolloClient.query({
      query: GET_CHAT_WITH_MESSAGES,
      variables: { uuid: chatUuidWithoutPrefix },
      fetchPolicy: 'network-only',
    });

    if (data?.getChat?.messages && data.getChat.messages.length > 0) {
      const lastAssistantMessage = [...data.getChat.messages].reverse().find((msg) => msg.role === 'assistant');
      const lastMessagePart = lastAssistantMessage
        ? last(lastAssistantMessage.parts as UIMessageWithMetadata['parts'])
        : undefined;
      if (lastMessagePart?.type === 'text' && typeof lastMessagePart.text === 'string') {
        return lastMessagePart.text.slice(0, 100) || chatTitleCallback;
      }
    }
  } catch (error) {
    browserLogger.debug('[Notification Helpers] Failed to fetch chat data for notification', {
      error,
      chatUuid,
    });
  }

  return chatTitleCallback;
}

/**
 * Updates the Apollo cache so an answered clarification round is visible
 * immediately on the next hydration, before the server snapshot catches up.
 */
export function markClarificationResolvedInCache({
  apolloClient,
  chatUuid,
  messageUuid,
  message,
  toolCallId,
  output,
  transcript,
}: {
  apolloClient: ApolloClient<unknown>;
  chatUuid: string;
  messageUuid: string;
  message?: CacheableUIMessage;
  toolCallId: string;
  output: unknown;
  transcript: string;
}): void {
  const targetMessageIds = new Set(
    [messageUuid, message?.id, message?.uuid].filter(
      (candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0,
    ),
  );

  for (const cacheUuid of new Set([normalizeChatUuid(chatUuid), chatUuid])) {
    try {
      const cachedData = apolloClient.readQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
      });

      if (!cachedData?.getChat?.messages) {
        continue;
      }

      let matchedExistingMessage = false;
      const updatedMessages = cachedData.getChat.messages.map((cachedMessage) => {
        const cachedMessageUuid = typeof cachedMessage.uuid === 'string' ? cachedMessage.uuid : undefined;

        if (!cachedMessageUuid || !targetMessageIds.has(cachedMessageUuid)) {
          return cachedMessage;
        }

        matchedExistingMessage = true;

        return {
          ...cachedMessage,
          parts: resolveClarificationParts(cachedMessage.parts, toolCallId, output),
          metadata: resolveClarificationMetadata(cachedMessage.metadata, toolCallId, transcript),
        };
      });

      const nextMessages =
        !matchedExistingMessage && message
          ? [
              ...updatedMessages,
              buildClarificationCacheMessage({
                chatUuid,
                message,
                toolCallId,
                output,
                transcript,
              }),
            ]
          : updatedMessages;

      apolloClient.writeQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
        data: {
          getChat: {
            ...cachedData.getChat,
            messages: nextMessages,
          },
        },
      });
    } catch (error) {
      browserLogger.debug('[Notification Helpers] Could not mark clarification as resolved', {
        error,
        chatUuid,
        cacheUuid,
        messageUuid,
        toolCallId,
      });
    }
  }
}

function resolveClarificationParts(parts: unknown, toolCallId: string, output: unknown) {
  return Array.isArray(parts)
    ? parts.map((part) => {
        if (!part || typeof part !== 'object' || part.type !== 'tool-clarification' || part.toolCallId !== toolCallId) {
          return part;
        }

        return {
          ...part,
          state: 'output-available',
          output,
        };
      })
    : parts;
}

function resolveClarificationMetadata(metadata: unknown, toolCallId: string, transcript: string) {
  const existingMetadata = metadata && typeof metadata === 'object' ? (metadata as Record<string, unknown>) : {};
  const existingClarification =
    existingMetadata.clarification && typeof existingMetadata.clarification === 'object'
      ? (existingMetadata.clarification as Record<string, unknown>)
      : {};

  return {
    ...existingMetadata,
    clarification: {
      ...existingClarification,
      state: 'clarification-resolved',
      activeToolCallId: toolCallId,
      resolvedTranscript: transcript,
    },
  };
}

function buildClarificationCacheMessage({
  chatUuid,
  message,
  toolCallId,
  output,
  transcript,
}: {
  chatUuid: string;
  message: CacheableUIMessage;
  toolCallId: string;
  output: unknown;
  transcript: string;
}): GetChatWithMessagesMessage {
  const rawMetadata =
    message.metadata && typeof message.metadata === 'object' ? (message.metadata as Record<string, unknown>) : {};
  const { files, reaction, ...backendMetadata } = rawMetadata;
  const normalizedReaction: GetChatWithMessagesMessage['reaction'] =
    reaction === 'THUMBS_UP' || reaction === 'THUMBS_DOWN' ? reaction : null;
  const messageCreatedAt =
    typeof message.createdAt === 'string'
      ? message.createdAt
      : message.createdAt instanceof Date
        ? message.createdAt.toISOString()
        : new Date().toISOString();
  const messageId = message.uuid ?? message.id;

  return {
    uuid: messageId,
    chatUuid: message.chatUuid ?? normalizeChatUuid(chatUuid),
    reaction: normalizedReaction,
    role: message.role,
    files: Array.isArray(files)
      ? files.map((file, fileIndex) => ({
          uuid:
            file && typeof file === 'object' && typeof file.uuid === 'string'
              ? file.uuid
              : buildOptimisticFileUuid(messageId, fileIndex),
          filename: file && typeof file === 'object' && typeof file.filename === 'string' ? file.filename : undefined,
        }))
      : [],
    parts: resolveClarificationParts(message.parts, toolCallId, output),
    metadata: resolveClarificationMetadata(backendMetadata, toolCallId, transcript),
    createdAt: messageCreatedAt,
  };
}

/**
 * Updates the Apollo cache to reflect a newly active stream.
 * Ensures both the sidebar chat list and the current chat query reflect
 * that the chat is in progress, which is especially important for
 * tool-driven follow-up turns that bypass the normal input submit handler.
 */
export function markChatStreamActive({
  apolloClient,
  chatUuid,
  userUuid,
  appType,
  streamId,
}: {
  apolloClient: ApolloClient<unknown>;
  chatUuid: string;
  userUuid: string;
  appType?: ChatAppType | null;
  streamId?: string;
}): void {
  const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);
  let effectiveStreamId = streamId ?? buildOptimisticStreamId(chatUuidWithoutPrefix);

  try {
    const cachedData = apolloClient.readQuery({
      query: GET_CHATS_BY_FILTERS,
      variables: { userUuid, appType },
    });

    if (cachedData?.chatsList) {
      const updatedChatsList = cachedData.chatsList.map((chat) => {
        if (chat.uuid === chatUuidWithoutPrefix || chat.uuid === chatUuid) {
          effectiveStreamId = chat.activeStreamId ?? effectiveStreamId;

          return {
            ...chat,
            activeStreamId: effectiveStreamId,
          };
        }

        return chat;
      });

      apolloClient.writeQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: { userUuid, appType },
        data: { chatsList: updatedChatsList },
      });
    }
  } catch (error) {
    browserLogger.debug('[Notification Helpers] Could not mark chats list stream as active', {
      error,
      chatUuid,
    });
  }

  for (const cacheUuid of new Set([chatUuidWithoutPrefix, chatUuid])) {
    try {
      const cachedData = apolloClient.readQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
      });

      if (!cachedData?.getChat) {
        continue;
      }

      effectiveStreamId = cachedData.getChat.activeStreamId ?? effectiveStreamId;

      apolloClient.writeQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
        data: {
          getChat: {
            ...cachedData.getChat,
            activeStreamId: effectiveStreamId,
          },
        },
      });
    } catch (error) {
      browserLogger.debug('[Notification Helpers] Could not mark chat stream as active', {
        error,
        chatUuid,
        cacheUuid,
      });
    }
  }
}

/**
 * Updates the Apollo cache to reflect stream completion.
 * Sets activeStreamId to null and optionally sets hasUnreadMessages.
 */
export function updateChatsListAfterStream({
  apolloClient,
  chatUuid,
  userUuid,
  appType,
  hasUnreadMessages,
}: {
  apolloClient: ApolloClient<unknown>;
  chatUuid: string;
  userUuid: string;
  appType?: ChatAppType | null;
  hasUnreadMessages?: boolean;
}): void {
  const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);

  try {
    const cachedData = apolloClient.readQuery({
      query: GET_CHATS_BY_FILTERS,
      variables: { userUuid, appType },
    });

    if (cachedData?.chatsList) {
      const updatedChatsList = cachedData.chatsList.map((chat) => {
        if (chat.uuid === chatUuidWithoutPrefix || chat.uuid === chatUuid) {
          return {
            ...chat,
            activeStreamId: null,
            ...(hasUnreadMessages !== undefined && { hasUnreadMessages }),
          };
        }
        return chat;
      });

      apolloClient.writeQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: { userUuid, appType },
        data: { chatsList: updatedChatsList },
      });
    }
  } catch (error) {
    // Cache update is not critical - log but don't fail
    browserLogger.debug('[Notification Helpers] Could not update Apollo cache', {
      error,
      chatUuid,
    });
  }

  for (const cacheUuid of new Set([chatUuidWithoutPrefix, chatUuid])) {
    try {
      const cachedData = apolloClient.readQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
      });

      if (!cachedData?.getChat) {
        continue;
      }

      apolloClient.writeQuery({
        query: GET_CHAT_WITH_MESSAGES,
        variables: { uuid: cacheUuid },
        data: {
          getChat: {
            ...cachedData.getChat,
            activeStreamId: null,
            ...(hasUnreadMessages !== undefined && { hasUnreadMessages }),
          },
        },
      });
    } catch (error) {
      browserLogger.debug('[Notification Helpers] Could not update current chat cache after stream', {
        error,
        chatUuid,
        cacheUuid,
      });
    }
  }
}
